import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory, useLocation } from "react-router";

import {
  Banner,
  Button,
  Combobox,
  Input,
  Spinner,
  toastError,
} from "@alpacahq/ui/old";

import { useGetAcatsContraBrokers } from "~/v2/api/hooks";
import type { ContraBroker } from "~/v2/api/rest/acats";
import { FAQ } from "~/v2/components/common/FAQ";
import { Path } from "~/v2/path";
import { Layout } from "../../common/Layout";
import type { AcatsLocationState } from "../../common/types";
import { getContraBrokerDisplayName } from "../../common/util";
import { BackButton } from "../BackButton";
import { ORIGINATING_BROKER_FAQ } from "./FAQ";
import {
  type OriginatingBrokerSchema,
  zOriginatingBrokerSchema,
} from "./schema";

const OriginatingBrokerPage = () => {
  const history = useHistory<AcatsLocationState>();
  const [isBrokerOther, setIsBrokerOther] = useState(false);

  const { state: locationState } = useLocation<AcatsLocationState>();

  const { data: brokerData, isLoading: isBrokersLoading } =
    useGetAcatsContraBrokers();

  const form = useForm<OriginatingBrokerSchema>({
    resolver: zodResolver(zOriginatingBrokerSchema),
    mode: "all",
    defaultValues: {
      dtc_number: locationState?.brokerNumber ?? "",
      name: locationState?.brokerName ?? "",
    },
  });

  const { watch, setValue, register } = form;

  const brokerDtcNumber = watch("dtc_number");
  const brokerName = watch("name");

  const comboBoxValue = isBrokerOther
    ? ({
        name: "Other",
        number: "",
        type: "BROKER",
      } as ContraBroker)
    : (brokerData?.brokers.find(
        (broker) =>
          broker.name === brokerName && broker.number === brokerDtcNumber,
      ) ?? null);

  const isDisabled = !brokerDtcNumber || !brokerName;

  const onBackClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER,
      state: undefined,
    });

  const onSubmitValid: SubmitHandler<OriginatingBrokerSchema> = ({
    name,
    dtc_number,
  }) => {
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO,
      state: {
        // remove spacing user could have included, already validated not empty
        brokerName: name.trim(),
        brokerNumber: dtc_number.trim(),
      },
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<OriginatingBrokerSchema> = (
    _errors,
  ) => {
    toastError("Please fill in all fields");
  };

  const onBrokerSelected = (broker: ContraBroker | null) => {
    setIsBrokerOther(false);
    setValue("name", broker?.name ?? "");
    setValue("dtc_number", broker?.number ?? "");
  };

  const onOtherBrokerClicked = () => {
    if (!isBrokerOther) {
      setValue("name", "");
      setValue("dtc_number", "");
      setIsBrokerOther(true);
    }
  };

  // if incoming params do not match any brokers set isBrokerOther to true and fill in form
  useEffect(() => {
    if (
      !brokerData?.brokers.length ||
      !locationState?.brokerName ||
      !locationState?.brokerNumber
    ) {
      return;
    }

    if (
      !brokerData.brokers.find(
        (broker) =>
          broker.name === locationState?.brokerName &&
          broker.number === locationState?.brokerNumber,
      )
    ) {
      setIsBrokerOther(true);
      setValue("name", locationState?.brokerName ?? "");
      setValue("dtc_number", locationState?.brokerNumber ?? "");
    }
  }, [brokerData?.brokers, locationState, setValue]);

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      <Layout
        title="Originating Broker"
        faq={<FAQ faq={ORIGINATING_BROKER_FAQ} />}
        className="mt-2 pt-0"
      >
        {isBrokersLoading ? (
          <Spinner className="mx-auto mt-10" />
        ) : (
          <form
            onSubmit={form.handleSubmit(onSubmitValid, onSubmitInvalid)}
            className="flex w-full max-w-md flex-col gap-4"
          >
            <Combobox<ContraBroker>
              variant="v2"
              label="Select Brokerage"
              placeholder="Select originating brokerage"
              dropdownButton
              options={brokerData?.brokers}
              getOptionKey={(option) => `${option.name}-${option.number}`}
              getOptionLabel={(option) =>
                getContraBrokerDisplayName(option.name)
              }
              value={comboBoxValue}
              onChange={onBrokerSelected}
              helpMsg={
                <Button
                  variant="v2/ghost"
                  onClick={onOtherBrokerClicked}
                  type="button"
                  className="h-fit"
                >
                  Don't see your broker?
                </Button>
              }
              openOnFocus
            />

            {isBrokerOther && (
              <>
                <Input
                  id="name"
                  label="Brokerage Name"
                  placeholder="Enter your broker name"
                  {...register("name")}
                />
                <Input
                  id="dtc_number"
                  label="DTC Number"
                  placeholder="Enter broker DTC number"
                  {...register("dtc_number")}
                />
                <Banner
                  variant="warning"
                  message="To ensure a successful Portfolio Transfer, please confirm the correct DTC number with your brokerage."
                />
              </>
            )}

            <div className="flex items-center justify-between gap-4">
              <Button variant="v2/stroke" onClick={onBackClicked} type="button">
                Previous
              </Button>
              <Button type="submit" variant="v2/filled" disabled={isDisabled}>
                Continue
              </Button>
            </div>
          </form>
        )}
      </Layout>
    </div>
  );
};

export default OriginatingBrokerPage;
